import { forwardRef, useRef, useEffect } from 'react';
import { styled } from 'goober';

import { useDebounce } from '@/hooks/use-debounce';
import { Input } from '@/ui/input/input';
import type { IconVariant } from '@/ui/icons/icon';

import { ActivateFiltersButton } from './activate-filters-button';
import { useTranslation } from 'react-i18next';

export const FilterSearchInput = ({
  className,
  placeholder,
  onChange,
  onRemove,
  onInputActivate,
  onInputDeactivate,
  isInputActivated,
  defaultValue,
  disabled = false,
  displayIconOnly = false,
  delay,
  controlHeight = '31px',
  inputWidth = '250px',
  searchInputIcon = 'SearchFilterMD',
  searchInputIconSize = '18px',
  searchInputIconColor,
}: {
  className?: string;
  placeholder: string;
  onChange: (searchPhrase: string) => void;
  onRemove: () => void;
  onInputActivate: () => void;
  onInputDeactivate: () => void;
  isInputActivated: boolean;
  defaultValue: string;
  disabled?: boolean;
  displayIconOnly?: boolean;
  delay?: number;
  controlHeight?: string;
  inputWidth?: string;
  searchInputIcon?: IconVariant;
  searchInputIconSize?: string;
  searchInputIconColor?: string;
}) => {
  const { t } = useTranslation('default');
  const handleInputChange = useDebounce(onChange, delay);

  const inputRef = useRef<HTMLInputElement>(null);

  // Sync input value when defaultValue changes (e.g. from URL params applied after mount)
  useEffect(() => {
    if (!inputRef.current) return;
    if (inputRef.current.value === defaultValue) return;

    inputRef.current.value = defaultValue;
  }, [defaultValue]);

  // Focus input when activated
  useEffect(() => {
    if (!isInputActivated) {
      return;
    }

    // Small delay to allow transition to start before focusing
    const timeoutId = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [isInputActivated]);

  const handleBlurInput = () => {
    if (!inputRef.current) return;

    if (inputRef.current.value === '') {
      onInputDeactivate();
    }
  };

  const clearInput = () => {
    if (!inputRef.current) return;

    inputRef.current.value = '';
    onRemove();
  };

  const currentValue = inputRef.current ? inputRef.current.value : '';

  return (
    <TransitionContainer className={className} $controlHeight={controlHeight}>
      <SearchInput
        ref={inputRef}
        placeholder={placeholder}
        onChange={e => handleInputChange(e.target.value)}
        onClearInput={clearInput}
        onBlur={handleBlurInput}
        showClearIcon={currentValue.length > 0}
        defaultValue={defaultValue}
        data-testid="filter-search-input"
        padding="4px 7px"
        fontSize="14px"
        leftIcon={searchInputIcon}
        iconSize={searchInputIconSize}
        iconColor={searchInputIconColor}
        isVisible={isInputActivated}
        $inputWidth={inputWidth}
        aria-hidden={!isInputActivated}
        tabIndex={isInputActivated ? 0 : -1}
      />
      <ActivateButton
        data-testid="filter-search-button"
        variant="text"
        startIcon="SearchFilterMD"
        onClick={onInputActivate}
        disabled={disabled}
        isVisible={!isInputActivated}
        $controlHeight={controlHeight}
        aria-hidden={isInputActivated}
        tabIndex={isInputActivated ? -1 : 0}
      >
        {displayIconOnly ? '' : t`searchText`}
      </ActivateButton>
    </TransitionContainer>
  );
};

const TransitionContainer = styled('div')<{ $controlHeight: string }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  height: ${({ $controlHeight }) => $controlHeight};
`;

const SearchInput = styled(Input, forwardRef)<{
  $inputWidth: string;
  isVisible: boolean;
}>`
  width: ${({ $inputWidth }) => $inputWidth};
  opacity: ${props => (props.isVisible ? '1' : '0')};
  transform: translateX(${props => (props.isVisible ? '0' : '-10px')});
  transition:
    opacity 0.2s ease-in-out,
    transform 0.2s ease-in-out;
  pointer-events: ${props => (props.isVisible ? 'auto' : 'none')};
  ${props =>
    !props.isVisible &&
    `
    position: absolute;
    right: 0;
  `}
`;

const ActivateButton = styled(ActivateFiltersButton)<{
  $controlHeight: string;
  isVisible: boolean;
}>`
  height: ${({ $controlHeight }) => $controlHeight};
  opacity: ${props => (props.isVisible ? '1' : '0')};
  transform: translateX(${props => (props.isVisible ? '0' : '10px')});
  transition:
    opacity 0.2s ease-in-out,
    transform 0.2s ease-in-out;
  pointer-events: ${props => (props.isVisible ? 'auto' : 'none')};
  ${props =>
    !props.isVisible &&
    `
    position: absolute;
    right: 0;
  `}
`;
